/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../../../util/TransactionUtil'
], (_, APIFactory, PropertiesProviderAPI, TransactionUtil) => {

	class VisPropertyProvider {

		constructor(options) {
			this.features = options.features;
			this.content = options.content;
			this.dashboard = options.dashboardAPI;
			this.features['Properties'].registerProvider(this.getAPI());
			this.model = this.content.getFeature('Models.internal').getWidgetModel();
			this.content.on('change:visualization:type', this.onVisualizationTypeChange, this);

		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [PropertiesProviderAPI]);
			}
			return this._api;
		}

		destroy() {
			this.content.off('change:visualization:type', this.onVisualizationTypeChange, this);
			this.content = null;
		}

		onVisualizationTypeChange() {
			this._propertyList = null;
			this._propertyLayoutList = null;
		}

		getWidget() {
			return this.content;
		}

		_getPropertyFromRawDefinition(name) {
			const definition = this.content.getFeature('Visualization').getDefinition();
			const rawProp = definition && definition.getProperty(name);
			if (rawProp) {
				return JSON.parse(JSON.stringify(rawProp));
			}
		}

		/**
		 * todo: we dont need this after we use new api events.
		 * @param {*} property
		 * @param {*} widget
		 */
		modifyPropertyWithSetterAndGetter(property) {
			// [livewidget-cleanup] todo: removed the dependenty from widget
			const propertySupport = this._getVisPropertySupport();
			_.extend(property, {
				getPropertyValue: () => propertySupport.getPropertyValue(property.id),
				setPropertyValue: (value, transactionToken) => {
					this.updateProperty({
						id: property.id,
						value: value
					}, transactionToken);
				}
			});
		}

		/**
		 * @returns true if the property is not a 'general' property (i.e. is a
		 * visualization property that can be overriden by a user)
		 */
		isAnOverridableVisProperty(property) {
			var commonProps = {
				'fillColor': true,
				'borderColor': true,
				'transparency': true,
				'showTitle': true,
				'saveTitle': true,
				'queryRefresh': true,
				'datagrid.viewOption': true, // managed by the content props
				'value.graphic.content': true, // managed by the content props
				'value.graphic.currentScaleOption': true, // managed by the content props
				'value.graphic.fillColor':true, // managed by the content props
				'value.graphic.borderColor':true // managed by the content props
			};
			return !commonProps[property];
		}

		updateProperty(property, transactionToken) {
			if (this.isAnOverridableVisProperty(property.id)) {
				const boardModel = this.dashboard.getFeature('internal').getBoardModel();
				const options = TransactionUtil.transactionTokenToOptions(transactionToken);
				const extendedOptions = _.extend({}, options, boardModel.getLanguageModelOptions(true));
				if(!extendedOptions.payloadData) {
					extendedOptions.payloadData = {};
				}
				extendedOptions.payloadData.skipUndoRedo = true;
				const widgetModel = this.content.getFeature('Models.internal').getWidgetModel();

				// Update the properties on the vismodel
				if (property.value === undefined) {
					// Collection::set does not correctly merge undefined values into
					// the existing property, so do so manually.
					const originalProperty = widgetModel.properties.get(property.id);
					originalProperty.set({
						value: undefined
					});
				} else {
					widgetModel.properties.set(property, _.extend({
						merge: true,
						remove: false
					}, extendedOptions));
				}
			}
		}

		_getVisPropertySupport() {
			return this.features['Visualization.legacy'].getLegacyManagers().visPropertySupport;
		}

		getPropertyList() {
			if (!this._propertyList) {
				let propertyListFromDefinition = this._getPropertyFromRawDefinition('propertyList');
				if (propertyListFromDefinition && propertyListFromDefinition.length > 0) {
					propertyListFromDefinition.forEach(property=> {

						if(this.isAnOverridableVisProperty(property.id)){

							this.modifyPropertyWithSetterAndGetter(property);

							if (property.editor && property.editor.uiControl && property.editor.uiControl.multilingual) {
								const translationService = this.dashboard.getFeature('TranslationService');
								if (translationService) {
									const { multilingualProperty } = this.model.getMultilingualAttribute(property.id) || {};
									translationService.processMultilingualProperty(property, multilingualProperty);
								}
							}
						}

					});
				} else {
					propertyListFromDefinition = [];
				}

				this._propertyList = propertyListFromDefinition;
			}
			return this._propertyList;
		}

		getPropertyLayoutList() {
			if (!this._propertyLayoutList) {
				const rawPropertyLayoutList = this._getPropertyFromRawDefinition('propertyLayoutList');
				this._propertyLayoutList = rawPropertyLayoutList ? rawPropertyLayoutList: [];
			}
			return this._propertyLayoutList;
		}

		reset() { }
	}

	return VisPropertyProvider;
});
